import { useState } from "react";
import {
  ArrowLeftRight,
  Euro,
  Plus,
  RectangleHorizontal,
  ScanSearch,
  Trash2,
} from "lucide-react";
import { useDeleteSlot } from "@/api/hooks/useSloty";
import { extractErrorMessage } from "@/api/client";
import { toast } from "@/components/ui/use-toast";
import { sklonuj } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { HranyNaKarte, slotMaHrany } from "@/components/slot/HranySlotu";
import { SlotHranyDialog } from "@/components/slot/SlotHranyDialog";
import { MaterialCenaDialog } from "@/components/cennik/MaterialCenaDialog";
import { CenaOverrideZnak } from "@/components/cennik/CenaOverrideZnak";
import { useCenaOverrideMap } from "@/api/hooks/useZakazkaCenaOverrides";
import { SlotEditDialog } from "@/components/slot/SlotEditDialog";
import { CollapsibleSection } from "@/components/zakazka/CollapsibleSection";
import type { Dielec, MaterialSlot } from "@/api/schemas";

export interface SlotListProps {
  zakazkaId: string;
  variantaId: string;
  sloty: MaterialSlot[];
  /** Slot IDs, ktoré používa aspoň jeden dielec — zvyšné sú „nepriradené". */
  pouziteSlotIds?: Set<string>;
  /** Otvorí kalkulačku skla (vstup zo slot dialógu). */
  onOpenSklo?: () => void;
  /** V tabovom paneli je titulok tab a roleta by bola druhá úroveň skladania. */
  collapsible?: boolean;
  /** Označiť v tabuľke riadky s týmto materiálom (KOLO 233). */
  onOznac?: (slot: MaterialSlot) => void;
  /** Dielce varianty — Odobrať z karty povie, koľko materiál používa. */
  dielce?: Dielec[];
}

export function SlotList({
  zakazkaId,
  variantaId,
  sloty,
  pouziteSlotIds,
  onOpenSklo,
  collapsible = true,
  onOznac,
  dielce = [],
}: SlotListProps): JSX.Element {
  const [editingSlot, setEditingSlot] = useState<MaterialSlot | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cenaEdit, setCenaEdit] = useState<string | null>(null);
  // Okno Hrany (KOLO 228) — id, aby po refetchi videlo čerstvý slot
  const [hranySlotId, setHranySlotId] = useState<string | null>(null);
  const [novySlot, setNovySlot] = useState<MaterialSlot | null>(null);
  const hranySlot =
    sloty.find((s) => s.id === hranySlotId) ??
    (novySlot?.id === hranySlotId ? novySlot : null);
  // Mapa už existuje kvôli cenám v detaile — nepribúda dopyt (KOLO 101)
  const overrideMap = useCenaOverrideMap(zakazkaId);
  const deleteMutation = useDeleteSlot(zakazkaId);

  /**
   * Odobrať materiál priamo z karty (KOLO 233) — user: *„tlačidlo vymazať
   * pri materiáli, že nemusím ísť do výmeny materiálu, ikona v pravom
   * rohu"*. Tá istá otázka ako v okne Materiál, s počtom dielcov.
   */
  async function odober(s: MaterialSlot): Promise<void> {
    const pocet = dielce.filter((d) => d.material_slot_id === s.id).length;
    const veta =
      pocet > 0
        ? `Používa ho ${pocet} ${sklonuj(pocet, ["dielec", "dielce", "dielcov"])} — prestanú ho používať.`
        : "Žiadny dielec ho nepoužíva.";
    if (!window.confirm(`Odobrať materiál ${s.slot_kod} zo zákazky? ${veta}`)) return;
    try {
      await deleteMutation.mutateAsync(s.id);
      toast({ title: `Materiál ${s.slot_kod} odobratý` });
    } catch (err) {
      toast({ variant: "destructive", title: "Chyba", description: extractErrorMessage(err) });
    }
  }

  const pridatBtn = (
    <Button
      size="sm"
      variant="ghost"
      className="h-7 w-7 p-0"
      onClick={() => setCreateOpen(true)}
      aria-label="Pridať materiál"
      title="Pridať materiál (M1, M2…)"
    >
      <Plus className="h-4 w-4" />
    </Button>
  );

  return (
    <PanelObal
      title="Materiály zákazky"
      action={pridatBtn}
      collapsible={collapsible}
    >
      {sloty.length === 0 ? (
        <p className="text-sm text-muted-foreground py-2">
          Žiadne materiály. Klikni na „+“ a pridaj M1, M2, …
        </p>
      ) : (
        <div className="space-y-2">
          {groupSloty(sloty).map(({ label, items }) => (
            <div key={label} className="space-y-1">
              <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {label}
              </h3>
              <ul className="space-y-1">
                {items.map((s) => {
                  // Slot má materiál, no nepoužíva ho žiaden dielec → zbytočný
                  const nepriradeny =
                    !!s.material &&
                    pouziteSlotIds != null &&
                    !pouziteSlotIds.has(s.id);
                  const maHrany = slotMaHrany(s);
                  // Klik na kartu = to, čo sa mení najčastejšie: hrany;
                  // materiál bez hrán (dĺžkový, sklo, masív, prázdny) =
                  // okno Materiál (variant A stránky návrhov, KOLO 228)
                  const otvorKartu = (): void => {
                    if (maHrany) setHranySlotId(s.id);
                    else setEditingSlot(s);
                  };
                  return (
                  <li
                    key={s.id}
                    className={
                      "relative rounded-md border" +
                      (nepriradeny
                        ? " border-l-2 border-l-amber-400 bg-amber-50/60 dark:bg-amber-950/30"
                        : "")
                    }
                  >
                    {/* Pravý horný roh (KOLO 233): štítok upravenej ceny,
                        Označiť v tabuľke a Odobrať — mimo tlačidla karty */}
                    <span className="absolute right-1.5 top-1.5 z-10 flex items-center gap-0.5">
                      {s.material ? (
                        <CenaOverrideZnak
                          zakazkaId={zakazkaId}
                          materialId={s.material.id}
                          nazov={s.material.nazov}
                          upravena={overrideMap.has(s.material.id)}
                        />
                      ) : null}
                      {onOznac && s.material ? (
                        <button
                          type="button"
                          aria-label={`Označiť v tabuľke ${s.slot_kod}`}
                          title="Označiť v tabuľke — všetky riadky s týmto materiálom"
                          className="rounded p-1 text-muted-foreground hover:bg-background hover:text-primary"
                          onClick={() => onOznac(s)}
                        >
                          <ScanSearch className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                      <button
                        type="button"
                        aria-label={`Odobrať materiál ${s.slot_kod}`}
                        title="Odobrať materiál zo zákazky"
                        disabled={deleteMutation.isPending}
                        className="rounded p-1 text-muted-foreground hover:bg-background hover:text-destructive disabled:opacity-50"
                        onClick={() => void odober(s)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                    <button
                      type="button"
                      onClick={otvorKartu}
                      title={
                        nepriradeny
                          ? "Materiál nie je priradený žiadnemu dielcu — je zbytočný alebo naň zabudol"
                          : maHrany
                            ? "Hrany materiálu"
                            : "Materiál — vymeniť dosku, popis"
                      }
                      className="w-full rounded-t-md px-3 pb-1.5 pt-2 text-left transition-colors hover:bg-accent"
                    >
                      <div
                        className={
                          "flex items-baseline gap-2" +
                          // miesto pre ikony vpravo hore, so štítkom
                          // „upravená cena ↺" viac (KOLO 233)
                          (s.material && overrideMap.has(s.material.id) ? " pr-40" : " pr-14")
                        }
                      >
                        <span className="font-mono font-medium">{s.slot_kod}</span>
                        {nepriradeny ? (
                          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-normal text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                            nepriradený
                          </span>
                        ) : s.label ? (
                          <span className="truncate text-xs text-muted-foreground">
                            {s.label}
                          </span>
                        ) : null}
                      </div>
                      <div
                        className="mt-0.5 truncate text-xs"
                        title={s.material?.nazov ?? undefined}
                      >
                        {s.material ? s.material.nazov : "(materiál nenastavený)"}
                      </div>
                      {maHrany ? <HranyNaKarte slotId={s.id} /> : null}
                    </button>
                    <div
                      role="group"
                      aria-label={`Akcie ${s.slot_kod}`}
                      className="flex gap-1 border-t px-1.5 py-1"
                    >
                      {s.material ? (
                        <TlacidloKarty
                          ikona={<Euro className="h-3.5 w-3.5" />}
                          text="Cena"
                          popis={`Cena ${s.slot_kod} — nákup, réžia, práca, odpad pre túto zákazku`}
                          onClick={() => setCenaEdit(s.material!.id)}
                        />
                      ) : null}
                      {maHrany ? (
                        <TlacidloKarty
                          ikona={<RectangleHorizontal className="h-3.5 w-3.5" />}
                          text="Hrany"
                          popis={`Hrany ${s.slot_kod} podľa hrúbky dielca`}
                          onClick={() => setHranySlotId(s.id)}
                        />
                      ) : null}
                      <TlacidloKarty
                        ikona={<ArrowLeftRight className="h-3.5 w-3.5" />}
                        text="Materiál"
                        popis={`Materiál ${s.slot_kod} — vymeniť dosku, popis, odobrať`}
                        onClick={() => setEditingSlot(s)}
                      />
                    </div>
                  </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      {cenaEdit ? (
        <MaterialCenaDialog
          materialId={cenaEdit}
          zakazkaId={zakazkaId}
          variantaId={variantaId}
          open
          onOpenChange={(o) => !o && setCenaEdit(null)}
        />
      ) : null}

      <SlotEditDialog
        zakazkaId={zakazkaId}
        variantaId={variantaId}
        slot={editingSlot}
        suggestedKod={suggestNextKod(sloty)}
        open={!!editingSlot || createOpen}
        onOpenChange={(o) => {
          if (!o) {
            setEditingSlot(null);
            setCreateOpen(false);
          }
        }}
        onOpenSklo={onOpenSklo}
        // Nový materiál s hranami → rovno okno Hrany (dovtedy ostalo
        // otvorené okno materiálu s tabuľkou dole)
        onCreated={(novy) => {
          if (!slotMaHrany(novy)) return;
          setNovySlot(novy);
          setHranySlotId(novy.id);
        }}
        onOtvorHrany={
          editingSlot
            ? () => {
                const id = editingSlot.id;
                setEditingSlot(null);
                setHranySlotId(id);
              }
            : undefined
        }
      />

      <SlotHranyDialog
        zakazkaId={zakazkaId}
        slot={hranySlot}
        open={!!hranySlot}
        onOpenChange={(o) => {
          if (!o) {
            setHranySlotId(null);
            setNovySlot(null);
          }
        }}
      />
    </PanelObal>
  );
}

/** Tlačidlo pod kartou materiálu — ikona + slovo, celé ťukateľné (mobil). */
function TlacidloKarty({
  ikona,
  text,
  popis,
  onClick,
}: {
  ikona: React.ReactNode;
  text: string;
  popis: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={popis}
      title={popis}
      className="inline-flex h-7 flex-1 items-center justify-center gap-1 rounded text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      {ikona}
      {text}
    </button>
  );
}

/**
 * V bočnom paneli s tabmi je titulkom tab, takže roleta by bola druhá úroveň
 * skladania — vtedy sa vykreslí len plochá sekcia s akciou.
 */
function PanelObal({
  title,
  action,
  collapsible,
  children,
}: {
  title: string;
  action: React.ReactNode;
  collapsible: boolean;
  children: React.ReactNode;
}): JSX.Element {
  if (collapsible) {
    return (
      <CollapsibleSection title={title} storageKey="sloty" defaultOpen action={action}>
        {children}
      </CollapsibleSection>
    );
  }
  return (
    <section className="px-2.5 py-1.5 space-y-1">
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] font-semibold uppercase tracking-wide text-primary">
          {title}
        </h3>
        {action}
      </div>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

/** Sloty do sekcií Plošné / Dĺžkové / Sklo — prázdne sekcie sa nezobrazujú. */
function groupSloty(
  sloty: MaterialSlot[],
): { label: string; items: MaterialSlot[] }[] {
  const skupiny: Record<string, MaterialSlot[]> = {
    "Plošné": [],
    "Dĺžkové": [],
    "Sklo": [],
  };
  for (const s of sloty) {
    if (s.material?.kategoria?.startsWith("SKLO")) skupiny["Sklo"]!.push(s);
    else if (s.material?.druh === "dlzkovy") skupiny["Dĺžkové"]!.push(s);
    else skupiny["Plošné"]!.push(s);
  }
  return Object.entries(skupiny)
    .filter(([, items]) => items.length > 0)
    .map(([label, items]) => ({ label, items }));
}

function suggestNextKod(sloty: MaterialSlot[]): string {
  const used = new Set(
    sloty
      .map((s) => s.slot_kod.match(/^M(\d+)$/)?.[1])
      .filter((n): n is string => !!n)
      .map(Number),
  );
  let n = 1;
  while (used.has(n)) n += 1;
  return `M${n}`;
}
